import React, { useState } from 'react';
import LayoutModal from '../LayoutModal';
import ThemeSelector from './ThemeSelector';
import ComponentSelector from './ComponentSelector';
import ComponentsList from './ComponentsList';
import { getLayout, getLayoutSlots } from '../../../config/layouts';

const LeftPanel = ({ 
  pageData, 
  updateLayout, 
  updateTheme, 
  onAddFromLibrary,
  onAddNewComponent,
  onAddFormComponent,
  onAddCardComponent,
  updateComponent, 
  deleteComponent,
  duplicateComponent,
  accordionState,
  onSaveToLibrary,
  componentRefreshTrigger
}) => {
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  
  const currentLayout = getLayout(pageData.layoutId);
  const layoutSlots = getLayoutSlots(pageData.layoutId);

  // Organize components by slot
  const componentsBySlot = {};
  layoutSlots.forEach(slot => {
    componentsBySlot[slot.id] = pageData.components.filter(
      comp => comp.slotId === slot.id
    );
  });

  // Components without slot assignment (exclude grid-nested components)
console.log('pageData.components:', pageData.components);

const unassignedComponents = pageData.components.filter(comp => {
  // ❌ ignore components that belong to a grid (nested)
  if (comp.isNested || comp.parentComponentId) {
    return false;
  }

  // ❌ ignore components properly assigned to a slot
  if (comp.slotId && layoutSlots.some(slot => slot.id === comp.slotId)) {
    return false;
  }

  // ✅ truly unassigned
  return true;
});


  const handleLayoutSelect = (newLayoutId) => {
    if (newLayoutId !== pageData.layoutId) {
      // Show warning if there are components
      if (pageData.components.length > 0) {
        const confirmChange = window.confirm(
          '⚠️ Changing the layout will require you to reassign components to the new slots. ' +
          'We\'ll try to map them automatically, but you may need to reorganize some content. Continue?'
        );
        if (!confirmChange) return;
      }
      updateLayout(newLayoutId);
    }
    setIsLayoutModalOpen(false);
  };

  // Handle adding component to specific slot
  const handleAddToSlot = (componentType, slotId) => {
    if (componentType === 'card') {
      onAddCardComponent?.(slotId);
      return;
    }
    onAddNewComponent(componentType, null, slotId);
  };

  return (
    <aside className="builder-panel builder-panel--palette">
      <div className="builder-panel-header">
        <h3>Page Configuration</h3>
        <p>Configure layout, theme, and add components</p>
      </div>

      {/* Layout Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <h4 style={{ fontSize: '12px', fontWeight: '600', margin: 0, color: '#374151' }}>
            Layout
          </h4>
          <button
            onClick={() => setIsLayoutModalOpen(true)}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: '1px solid #3B82F6',
              backgroundColor: '#FFFFFF',
              color: '#3B82F6',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#EFF6FF';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#FFFFFF';
            }}
          >
            Change Layout
          </button>
        </div>

        {/* Current Layout Display */}
        <div
          style={{
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB'
          }}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={{ fontSize: '18px' }}>📐</span>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#111827' 
            }}>
              {currentLayout.name}
            </span>
          </div>
          <p style={{ 
            fontSize: '11px', 
            color: '#6B7280', 
            margin: '4px 0 0 26px',
            lineHeight: '1.4'
          }}>
            {currentLayout.description}
          </p>
          <div style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#3B82F6',
            fontWeight: '600',
            marginLeft: '26px'
          }}>
            {layoutSlots.length} Slots Available
          </div>
        </div>
      </div>

      {/* Layout Modal */}
      <LayoutModal
        isOpen={isLayoutModalOpen}
        onClose={() => setIsLayoutModalOpen(false)}
        currentLayoutId={pageData.layoutId}
        onSelectLayout={handleLayoutSelect}
      />

      {/* Theme Section */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
          Theme
        </h4>
        <ThemeSelector 
          currentThemeId={pageData.themeId} 
          onThemeChange={updateTheme} 
        />
      </div>

      {/* Slot-Based Component Management */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', margin: 0, color: '#111827' }}>
            Content Slots
          </h4>
          <span style={{ 
            fontSize: '11px', 
            fontWeight: '600', 
            color: '#6B7280',
            backgroundColor: '#F3F4F6',
            padding: '3px 8px',
            borderRadius: '4px'
          }}>
            {pageData.components.length} Components
          </span>
        </div>

        {/* Slot List */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '10px'
        }}>
          {layoutSlots.map((slot, index) => {
            const slotComponents = componentsBySlot[slot.id] || [];
            const isEmpty = slotComponents.length === 0;
            const isRequired = slot.required;

            return (
              <div
                key={slot.id}
                style={{
                  border: isEmpty && isRequired ? '2px dashed #F59E0B' : '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '10px',
                  backgroundColor: isEmpty ? '#FAFAFA' : '#FFFFFF',
                  transition: 'all 0.2s'
                }}
              >
                {/* Slot Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: slotComponents.length > 0 ? '8px' : 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      fontSize: '16px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: isEmpty ? '#F3F4F6' : '#3B82F6',
                      color: isEmpty ? '#6B7280' : '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}>
                      {index + 1}
                    </span>
                    <div>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#111827',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        {slot.name}
                        {isRequired && (
                          <span style={{ color: '#DC2626', fontSize: '14px' }}>*</span>
                        )}
                      </div>
                      {isEmpty && (
                        <div style={{ 
                          fontSize: '10px', 
                          color: '#9CA3AF',
                          marginTop: '2px'
                        }}>
                          {slot.placeholder}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: isEmpty ? '#9CA3AF' : '#3B82F6',
                    backgroundColor: isEmpty ? '#F3F4F6' : '#EFF6FF',
                    padding: '3px 8px',
                    borderRadius: '4px'
                  }}>
                    {slotComponents.length}/{slot.maxComponents || '∞'}
                  </div>
                </div>

                {/* Slot Components */}
                {slotComponents.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '6px',
                    marginTop: '8px'
                  }}>
                    {slotComponents.map((component, compIndex) => (
                      <div
                        key={component.id}
                        style={{
                          padding: '8px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '6px',
                          border: '1px solid #E5E7EB',
                          fontSize: '12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#6B7280' }}>#{compIndex + 1}</span>
                          <span style={{ fontWeight: '600', color: '#374151' }}>
                            {component.type}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => accordionState.expandAccordion(component.id)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: 'none',
                              backgroundColor: '#E5E7EB',
                              color: '#374151',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteComponent(component.id)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: 'none',
                              backgroundColor: '#FEE2E2',
                              color: '#DC2626',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Component to Slot - Component Selector */}
                <div style={{ marginTop: slotComponents.length > 0 ? '8px' : '8px' }}>
                  <div style={{
                    border: isEmpty && isRequired ? '2px dashed #F59E0B' : '1px dashed #D1D5DB',
                    borderRadius: '8px',
                    padding: '6px',
                    backgroundColor: isEmpty && isRequired ? '#FFFBEB' : '#F9FAFB'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: '600', 
                      color: isEmpty && isRequired ? '#92400E' : '#6B7280',
                      marginBottom: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      ➕ Add to {slot.name}
                      {slot.suggestedComponents && slot.suggestedComponents.length > 0 && (
                        <span style={{ 
                          fontSize: '9px', 
                          color: '#9CA3AF',
                          fontWeight: '400'
                        }}>
                          (Suggested: {slot.suggestedComponents.slice(0, 3).join(', ')})
                        </span>
                      )}
                    </div>
                    <ComponentSelector 
                      onAddComponent={(componentType) => handleAddToSlot(componentType, slot.id)}
                      onAddCardComponent={() => onAddCardComponent?.(slot.id)}
                      onAddFromLibrary={(component) => {
                        onAddFromLibrary(component);
                        setTimeout(() => {
                          const addedComponent = pageData.components[pageData.components.length - 1];
                          if (addedComponent) {
                            updateComponent(addedComponent.id, { slotId: slot.id });
                          }
                        }, 100);
                      }}
                      onAddFormComponent={() => {
                        onAddFormComponent(slot.id);
                      }}
                      refreshTrigger={componentRefreshTrigger}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Unassigned Components Warning */}
        {unassignedComponents.length > 0 && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B'
          }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#92400E',
              marginBottom: '4px'
            }}>
              ⚠️ {unassignedComponents.length} Unassigned Component{unassignedComponents.length > 1 ? 's' : ''}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#78350F' 
            }}>
              These components need to be assigned to slots
            </div>
          </div>
        )}
      </div>

      {/* Quick Add Component */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
          Quick Add Component
        </h4>
        <ComponentSelector 
          onAddComponent={onAddNewComponent}
          onAddFromLibrary={onAddFromLibrary}
          onAddFormComponent={onAddFormComponent}
          onAddCardComponent={() => onAddCardComponent?.(null)}
          refreshTrigger={componentRefreshTrigger}
        />
      </div>

      {/* Component Configuration Forms */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
          Component Configuration ({pageData.components.length})
        </h4>
        <ComponentsList 
          components={pageData.components}
          updateComponent={updateComponent}
          deleteComponent={deleteComponent}
          duplicateComponent={duplicateComponent}
          accordionState={accordionState}
          onSaveToLibrary={onSaveToLibrary}
          layoutId={pageData.layoutId}
          onAddComponent={onAddNewComponent}
          onAddFormComponent={onAddFormComponent}
          onAddCardComponent={onAddCardComponent}
          onAddFromLibrary={onAddFromLibrary}
        />
      </div>
    </aside>
  );
};

export default LeftPanel;
